import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Save as SaveIcon,
  Send as SendIcon,
  ArrowBack as ArrowBackIcon,
  Description as DescriptionIcon,
  History as HistoryIcon,
  RateReview as RateReviewIcon,
  AccountTree as AccountTreeIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Undo as UndoIcon,
} from '@mui/icons-material';
import PageWithHeader from '../common/PageWithHeader';
import { useRouter } from '../../hooks/useRouter';
import { useAppSelector } from '../../redux/hooks';
import { getAvailableQuartersForSubmission, getDefaultQuarterSelection, isQuarterSubmissionAllowed, getQuarterSubmissionMessage } from '../../utils/periodUtil';
import { useCreateBSCMutation, useSubmitBSCMutation, useUpdateBSCMutation, useGetMyBSCSubmissionsQuery, useRevertBSCToDraftMutation } from '../../redux/balancedScorecardApi';
import {
  BSCPerspectiveScore,
  BSCKRAScore,
  DEFAULT_BSC_PERSPECTIVES,
  UNIT_OPTIONS,
  PERFORMANCE_RATINGS,
  calculateKRAScores,
  getGradeFromScore,
  BalancedScorecard,
} from './bscTypes';

// Helper function to format numbers with thousand separators
const formatNumber = (value: number | undefined): string => {
  if (value === undefined || value === null || isNaN(value)) return '';
  return value.toLocaleString('en-US');
};

// Helper function to parse formatted number string back to number
const parseFormattedNumber = (value: string): number => {
  // Remove commas and parse
  const cleanValue = value.replace(/,/g, '');
  return parseFloat(cleanValue) || 0;
};

// Custom component for formatted number input that handles cursor position correctly
interface FormattedNumberInputProps {
  value: number | undefined;
  onChange: (value: number) => void;
  disabled?: boolean;
  label: string;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
}

const FormattedNumberInput: React.FC<FormattedNumberInputProps> = ({
  value,
  onChange,
  disabled = false,
  label,
  startAdornment,
  endAdornment,
}) => {
  // Local state for the input value while editing
  const [localValue, setLocalValue] = useState<string>('');
  const [isFocused, setIsFocused] = useState(false);

  // Update local value when prop changes and not focused
  useEffect(() => {
    if (!isFocused) {
      setLocalValue(value !== undefined && value !== null && !isNaN(value) ? formatNumber(value) : '');
    }
  }, [value, isFocused]);

  const handleFocus = () => {
    setIsFocused(true);
    // Show raw number without formatting when focused
    setLocalValue(value !== undefined && value !== null && !isNaN(value) && value !== 0 ? value.toString() : '');
  };

  const handleBlur = () => {
    setIsFocused(false);
    // Parse and update the value
    const parsedValue = parseFormattedNumber(localValue);
    onChange(parsedValue);
    // Format the display value
    setLocalValue(parsedValue ? formatNumber(parsedValue) : '');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    // Allow only digits, decimal point, and minus sign
    const sanitized = inputValue.replace(/[^\d.-]/g, '');
    setLocalValue(sanitized);
    // Update parent state immediately for calculations
    const parsedValue = parseFloat(sanitized) || 0;
    onChange(parsedValue);
  };

  return (
    <TextField
      fullWidth
      label={label}
      value={localValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      disabled={disabled}
      size="small"
      inputProps={{ inputMode: 'decimal' }}
      InputProps={{
        startAdornment: startAdornment,
        endAdornment: endAdornment,
      }}
    />
  );
};

// Empty KRA template
const createEmptyKRA = (): BSCKRAScore => ({
  objective: '',
  measure: '',
  weight: 0,
  unitOfMeasure: 'percentage',
  measurementDirection: 'maximize', // Default: higher is better
  isExtraDuty: false, // Default: not an extra duty
  targetValue: 0,
  actualValue: 0,
  performance: 0,
  score: 0,
  weightedScore: 0,
});

// Perspective Section Component
interface PerspectiveSectionProps {
  perspective: BSCPerspectiveScore;
  perspectiveIndex: number;
  onUpdatePerspective: (index: number, perspective: BSCPerspectiveScore) => void;
  disabled?: boolean;
}

const PerspectiveSection: React.FC<PerspectiveSectionProps> = ({
  perspective,
  perspectiveIndex,
  onUpdatePerspective,
  disabled = false,
}) => {
  const [expanded, setExpanded] = useState(true);

  const handleWeightChange = (value: number) => {
    onUpdatePerspective(perspectiveIndex, {
      ...perspective,
      weight: value,
    });
  };

  const handleAddKRA = () => {
    onUpdatePerspective(perspectiveIndex, {
      ...perspective,
      kraScores: [...perspective.kraScores, createEmptyKRA()],
    });
  };

  const handleUpdateKRA = (kraIndex: number, field: keyof BSCKRAScore, value: any) => {
    const updatedKRAs = [...perspective.kraScores];
    updatedKRAs[kraIndex] = {
      ...updatedKRAs[kraIndex],
      [field]: value,
    };
    onUpdatePerspective(perspectiveIndex, {
      ...perspective,
      kraScores: updatedKRAs,
    });
  };

  const handleDeleteKRA = (kraIndex: number) => {
    const updatedKRAs = perspective.kraScores.filter((_, index) => index !== kraIndex);
    onUpdatePerspective(perspectiveIndex, {
      ...perspective,
      kraScores: updatedKRAs,
    });
  };

  // Calculate total weight for KRAs in this perspective
  const totalKRAWeight = perspective.kraScores.reduce((sum, kra) => sum + (kra.weight || 0), 0);
  const normalKRAWeight = perspective.kraScores
    .filter(kra => !kra.isExtraDuty)
    .reduce((sum, kra) => sum + (kra.weight || 0), 0);
  const extraDutyWeight = perspective.kraScores
    .filter(kra => kra.isExtraDuty)
    .reduce((sum, kra) => sum + (kra.weight || 0), 0);
  const hasExtraDuty = perspective.kraScores.some(kra => kra.isExtraDuty);
  
  const weightExact = normalKRAWeight === 100 || perspective.kraScores.length === 0;
  const weightExceeds = totalKRAWeight > 100;
  // Invalid only if normal (non-extra duty) KRA weights exceed 100%
  const normalWeightExceedsLimit = normalKRAWeight > 100;

  // Get perspective color based on name
  const getPerspectiveColor = (name: string) => {
    switch (name) {
      case 'Financial': return '#1976d2';
      case 'Customer': return '#388e3c';
      case 'Internal Processes': return '#f57c00';
      case 'Learning & Growth': return '#7b1fa2';
      default: return '#757575';
    }
  };

  return (
    <Card sx={{ mb: 3, border: `2px solid ${getPerspectiveColor(perspective.name)}` }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          backgroundColor: getPerspectiveColor(perspective.name),
          color: 'white',
          cursor: 'pointer',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="h6" sx={{ color: 'white' }}>{perspective.name}</Typography>
          <Chip
            label={`Weight: ${perspective.weight}%`}
            size="small"
            sx={{ backgroundColor: 'white', color: getPerspectiveColor(perspective.name) }}
          />
          <Chip
            label={`Goal Weight: ${totalKRAWeight}%${hasExtraDuty ? ` (${normalKRAWeight}% + ${extraDutyWeight}% extra)` : ''}`}
            size="small"
            sx={{ 
              backgroundColor: normalWeightExceedsLimit ? '#ffebee' : weightExact ? 'rgba(255,255,255,0.9)' : normalKRAWeight < 100 ? '#fff3e0' : '#e8f5e9',
              color: normalWeightExceedsLimit ? '#d32f2f' : getPerspectiveColor(perspective.name),
              border: normalWeightExceedsLimit ? '2px solid #d32f2f' : weightExact ? 'none' : normalKRAWeight < 100 ? '1px solid #ff9800' : '1px solid #4caf50',
              fontWeight: normalWeightExceedsLimit ? 'bold' : 'normal'
            }}
          />
          {perspective.perspectiveTotal !== undefined && (
            <Chip
              label={`Score: ${perspective.perspectiveTotal.toFixed(2)}%`}
              size="small"
              sx={{ backgroundColor: 'rgba(255,255,255,0.9)', color: getPerspectiveColor(perspective.name) }}
            />
          )}
        </Stack>
        <IconButton sx={{ color: 'white' }}>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <CardContent>
          <Stack spacing={3}>
            {/* Perspective Weight */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TextField
                label="Perspective Weight"
                type="number"
                value={perspective.weight}
                onChange={(e) => handleWeightChange(parseFloat(e.target.value) || 0)}
                disabled={disabled}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                sx={{ width: 200 }}
              />
              {!weightExact && normalKRAWeight < 100 && (
                <Alert severity="warning" sx={{ flexGrow: 1 }}>
                  Normal goal weights should total 100% (currently {normalKRAWeight}%){hasExtraDuty ? ` + ${extraDutyWeight}% extra duties` : ''}
                </Alert>
              )}
              {normalWeightExceedsLimit && (
                <Alert severity="error" sx={{ flexGrow: 1 }}>
                  Normal goal weights ({normalKRAWeight}%) exceed 100%! Reduce weights or mark some goals as "Extra Duty".
                </Alert>
              )}
              {weightExact && hasExtraDuty && (
                <Alert severity="success" sx={{ flexGrow: 1 }}>
                  Goal weights: {normalKRAWeight}% normal + {extraDutyWeight}% extra duties = {totalKRAWeight}% total ✓
                </Alert>
              )}
            </Box>

            {/* KRA Cards */}
            <Stack spacing={2}>
              {perspective.kraScores.map((kra, kraIndex) => (
                <Paper 
                  key={kraIndex} 
                  variant="outlined" 
                  sx={{ 
                    p: 2, 
                    backgroundColor: kra.isExtraDuty ? '#fffbf0' : '#fafafa',
                    border: kra.isExtraDuty ? '2px solid #ffc107' : '1px solid rgba(0, 0, 0, 0.12)',
                    '&:hover': { backgroundColor: kra.isExtraDuty ? '#fff8e1' : '#f5f5f5' }
                  }}
                >
                  {/* KRA Header */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Goal #{kraIndex + 1}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Button
                        size="small"
                        variant={kra.isExtraDuty ? "contained" : "outlined"}
                        onClick={() => handleUpdateKRA(kraIndex, 'isExtraDuty', !kra.isExtraDuty)}
                        disabled={disabled}
                        startIcon={kra.isExtraDuty ? <StarIcon /> : <StarBorderIcon />}
                        sx={{ 
                          minWidth: 120,
                          bgcolor: kra.isExtraDuty ? '#ffc107' : 'transparent',
                          color: kra.isExtraDuty ? '#5d4037' : '#ffc107',
                          borderColor: '#ffc107',
                          fontWeight: 'bold',
                          '&:hover': {
                            bgcolor: kra.isExtraDuty ? '#ffca28' : 'rgba(255, 193, 7, 0.1)',
                            borderColor: '#ffc107',
                          }
                        }}
                      >
                        {kra.isExtraDuty ? 'Extra Duty' : 'Extra?'}
                      </Button>
                      <Chip 
                        label={`Weight: ${kra.weight || 0}%`} 
                        size="small" 
                        color={kra.weight > 0 ? 'primary' : 'default'}
                        variant={kra.weight > 0 ? 'filled' : 'outlined'}
                      />
                      <Chip 
                        label={`Score: ${(kra.weightedScore || 0).toFixed(2)}`} 
                        size="small" 
                        color={(kra.weightedScore || 0) > 0 ? 'success' : 'default'}
                        variant={(kra.weightedScore || 0) > 0 ? 'filled' : 'outlined'}
                      />
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteKRA(kraIndex)}
                        disabled={disabled}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>

                  {/* Row 1: Goal & Measure */}
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Goal"
                        multiline
                        minRows={2}
                        maxRows={4}
                        value={kra.objective}
                        onChange={(e) => handleUpdateKRA(kraIndex, 'objective', e.target.value)}
                        disabled={disabled}
                        placeholder="What do you want to achieve?"
                        helperText="Define a clear, measurable goal"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Measure"
                        multiline
                        minRows={2}
                        maxRows={4}
                        value={kra.measure}
                        onChange={(e) => handleUpdateKRA(kraIndex, 'measure', e.target.value)}
                        disabled={disabled}
                        placeholder="How will success be measured?"
                        helperText="How will this be measured?"
                      />
                    </Grid>
                  </Grid>

                  {/* Row 2: Weight, Unit, Direction, Target, Actual */}
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6} sm={4} md={2}>
                      <TextField
                        fullWidth
                        label="Weight"
                        type="number"
                        value={kra.weight || ''}
                        onChange={(e) => handleUpdateKRA(kraIndex, 'weight', parseFloat(e.target.value) || 0)}
                        disabled={disabled}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        }}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Unit</InputLabel>
                        <Select
                          value={kra.unitOfMeasure}
                          label="Unit"
                          onChange={(e: SelectChangeEvent) => handleUpdateKRA(kraIndex, 'unitOfMeasure', e.target.value)}
                          disabled={disabled}
                        >
                          {UNIT_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Direction</InputLabel>
                        <Select
                          value={kra.measurementDirection || 'maximize'}
                          label="Direction"
                          onChange={(e: SelectChangeEvent) => handleUpdateKRA(kraIndex, 'measurementDirection', e.target.value)}
                          disabled={disabled}
                        >
                          <MenuItem value="maximize">↑ Higher</MenuItem>
                          <MenuItem value="minimize">↓ Lower</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6} sm={6} md={3}>
                      <FormattedNumberInput
                        label="Target"
                        value={kra.targetValue}
                        onChange={(val) => handleUpdateKRA(kraIndex, 'targetValue', val)}
                        disabled={disabled}
                        startAdornment={kra.unitOfMeasure === 'currency' ? <InputAdornment position="start">$</InputAdornment> : undefined}
                        endAdornment={kra.unitOfMeasure === 'percentage' ? <InputAdornment position="end">%</InputAdornment> : undefined}
                      />
                    </Grid>
                    <Grid item xs={6} sm={6} md={3}>
                      <FormattedNumberInput
                        label="Actual"
                        value={kra.actualValue}
                        onChange={(val) => handleUpdateKRA(kraIndex, 'actualValue', val)}
                        disabled={disabled}
                        startAdornment={kra.unitOfMeasure === 'currency' ? <InputAdornment position="start">$</InputAdornment> : undefined}
                        endAdornment={kra.unitOfMeasure === 'percentage' ? <InputAdornment position="end">%</InputAdornment> : undefined}
                      />
                    </Grid>
                  </Grid>

                  {/* Row 4: Calculated Results */}
                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      p: 1.5, 
                      backgroundColor: 'white',
                      display: 'flex',
                      justifyContent: 'space-around',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 2
                    }}
                  >
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Performance
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 'bold',
                          color: (kra.performance || 0) >= 100 ? 'success.main' : 
                                 (kra.performance || 0) >= 80 ? 'warning.main' : 'error.main',
                        }}
                      >
                        {(kra.performance || 0).toFixed(1)}%
                      </Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem />
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Score
                      </Typography>
                      <Typography variant="h6">
                        {((kra.score || 0)/100).toFixed(3)}
                      </Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem />
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Weighted Score
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                        {(kra.weightedScore || 0).toFixed(3)}
                      </Typography>
                    </Box>
                  </Paper>
                </Paper>
              ))}
              
              {perspective.kraScores.length === 0 && (
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    p: 4, 
                    textAlign: 'center',
                    backgroundColor: '#fafafa',
                    border: '2px dashed #e0e0e0'
                  }}
                >
                  <Typography color="text.secondary" gutterBottom>
                    No goals added yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Click "Add Goal" below to start defining your goals
                  </Typography>
                </Paper>
              )}
            </Stack>

            {/* Add KRA Button */}
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddKRA}
              disabled={disabled}
            >
              Add Goal
            </Button>
          </Stack>
        </CardContent>
      </Collapse>
    </Card>
  );
};

// Score Summary Component
interface ScoreSummaryProps {
  perspectives: BSCPerspectiveScore[];
  totalWeightedScore: number;
  finalGrade: string;
  bonusEntitlement: number;
}

const ScoreSummary: React.FC<ScoreSummaryProps> = ({
  perspectives,
  totalWeightedScore,
  finalGrade,
  bonusEntitlement,
}) => {
  const rating = getGradeFromScore(totalWeightedScore);
  const totalPerspectiveWeight = perspectives.reduce((sum, p) => sum + p.weight, 0);

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Performance Summary
      </Typography>
      <Divider sx={{ mb: 2 }} />

      <Grid container spacing={3}>
        {/* Perspective Breakdown */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Perspective Scores
          </Typography>
          {perspectives.map((perspective, index) => (
            <Box key={index} sx={{ mb: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2">{perspective.name}</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {(perspective.perspectiveTotal || 0).toFixed(2)}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.min((perspective.perspectiveTotal || 0) / perspective.weight * 100, 100)}
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
          ))}
          {totalPerspectiveWeight > 100 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Perspective weights exceed 100% ({totalPerspectiveWeight}%) - includes extra duties
            </Alert>
          )}
          {totalPerspectiveWeight < 100 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Perspective weights should total at least 100% (currently {totalPerspectiveWeight}%)
            </Alert>
          )}
        </Grid>

        {/* Final Score & Grade */}
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              textAlign: 'center',
              p: 3,
              borderRadius: 2,
              backgroundColor: rating?.colorCode || '#f5f5f5',
              color: 'white',
              textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
            }}
          >
            <Typography variant="h2" sx={{ fontWeight: 'bold' }}>
              {finalGrade}
            </Typography>
            <Typography variant="h4">
              {totalWeightedScore.toFixed(1)}%
            </Typography>
            <Typography variant="body1" sx={{ mt: 1 }}>
              {rating?.description}
            </Typography>
            <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.3)' }} />
            <Typography variant="h6">
              Bonus Entitlement: {bonusEntitlement}%
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {/* Rating Scale Reference */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Performance Rating Scale
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {PERFORMANCE_RATINGS.map((r) => (
            <Tooltip
              key={r.grade}
              title={`${r.minPercentage}% - ${r.maxPercentage === 999 ? '∞' : r.maxPercentage}% | Bonus: ${r.bonusEntitlement}%`}
            >
              <Chip
                label={r.grade}
                size="small"
                sx={{
                  backgroundColor: r.colorCode,
                  color: 'white',
                  fontWeight: r.grade === finalGrade ? 'bold' : 'normal',
                  border: r.grade === finalGrade ? '2px solid black' : 'none',
                }}
              />
            </Tooltip>
          ))}
        </Box>
      </Box>
    </Paper>
  );
};

// Main BSC Component
const BalancedScorecardForm: React.FC = () => {
  const router = useRouter();
  const currentUser = useAppSelector(state => state.auth.user);
  const isAdmin = currentUser?.roles?.includes('admin');

  // API queries and mutations
  const { data: mySubmissions, isLoading: loadingSubmissions } = useGetMyBSCSubmissionsQuery();
  const [createBSC] = useCreateBSCMutation();
  const [updateBSC] = useUpdateBSCMutation();
  const [submitBSC] = useSubmitBSCMutation();
  const [revertToDraft] = useRevertBSCToDraftMutation();

  // State
  const [reviewPeriod, setReviewPeriod] = useState<string>('');
  const [perspectives, setPerspectives] = useState<BSCPerspectiveScore[]>(
    DEFAULT_BSC_PERSPECTIVES.map(p => ({ ...p, kraScores: [] }))
  );
  const [employeeComments, setEmployeeComments] = useState<string>('');
  const [status, setStatus] = useState<string>('draft');
  const [saving, setSaving] = useState(false);
  const [existingBSCId, setExistingBSCId] = useState<string | null>(null);
  const [managerComments, setManagerComments] = useState<string>('');
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
  
  // Use ref to track saving state for autosave effect (avoids re-triggering)
  const savingRef = useRef(false);
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Compute available quarters (filter out submitted ones)
  // Shows Q1-Q4 2025 plus current year quarters
  const availableQuarters = useMemo(() => {
    return getAvailableQuartersForSubmission(mySubmissions);
  }, [mySubmissions]);

  // Set default quarter when submissions load
  useEffect(() => {
    if (!loadingSubmissions && availableQuarters.length > 0 && !reviewPeriod) {
      const defaultQuarter = getDefaultQuarterSelection(availableQuarters);
      if (defaultQuarter) {
        setReviewPeriod(defaultQuarter);
      }
    }
  }, [loadingSubmissions, availableQuarters, reviewPeriod]);

  // Find existing BSC for selected period
  const existingBSC = useMemo(() => {
    if (!reviewPeriod || !mySubmissions) return null;
    return mySubmissions.find(bsc => bsc.reviewPeriod === reviewPeriod) || null;
  }, [reviewPeriod, mySubmissions]);

  // Load existing BSC data when period changes
  useEffect(() => {
    if (existingBSC) {
      // Populate form with existing BSC data
      setExistingBSCId(existingBSC.id || null);
      setStatus(existingBSC.status || 'draft');
      setEmployeeComments(existingBSC.employeeComments || '');
      setManagerComments(existingBSC.managerComments || '');
      
      // Populate perspectives with existing data
      if (existingBSC.perspectives && existingBSC.perspectives.length > 0) {
        setPerspectives(existingBSC.perspectives);
      } else {
        setPerspectives(DEFAULT_BSC_PERSPECTIVES.map(p => ({ ...p, kraScores: [] })));
      }
    } else {
      // Reset form for new period
      setExistingBSCId(null);
      setStatus('draft');
      setEmployeeComments('');
      setManagerComments('');
      setPerspectives(DEFAULT_BSC_PERSPECTIVES.map(p => ({ ...p, kraScores: [] })));
    }
  }, [existingBSC]);

  // Calculate scores whenever perspectives change
  const calculatedData = useMemo(() => {
    return calculateKRAScores(perspectives);
  }, [perspectives]);

  // Determine if form should be read-only (moved up for autosave check)
  const isEditable = status === 'draft' || status === 'rejected';

  // Check if quarter has ended and submission is allowed
  const canSubmitQuarter = useMemo(() => {
    return isQuarterSubmissionAllowed(reviewPeriod);
  }, [reviewPeriod]);

  // Get the quarter submission restriction message
  const quarterSubmissionMessage = useMemo(() => {
    return getQuarterSubmissionMessage(reviewPeriod);
  }, [reviewPeriod]);

  // Autosave effect - debounced save when form data changes
  // Uses refs to avoid re-triggering on save state changes
  useEffect(() => {
    // Only autosave if:
    // 1. A period is selected
    // 2. The form is editable (draft or rejected)
    // 3. Autosave is enabled
    // 4. Not currently saving (check ref, not state)
    if (!reviewPeriod || !isEditable || !autoSaveEnabled || savingRef.current) {
      return;
    }

    // Clear any existing timeout
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    // Debounce the save operation - 5 seconds for seamless typing
    autoSaveTimeoutRef.current = setTimeout(async () => {
      if (savingRef.current) return; // Double-check before saving
      
      savingRef.current = true;
      setSaving(true);
      try {
        const bscData: Partial<BalancedScorecard> = {
          reviewPeriod,
          periodType: 'quarterly',
          perspectives: calculatedData.perspectives,
          totalWeightedScore: calculatedData.totalWeightedScore,
          finalGrade: calculatedData.finalGrade,
          bonusEntitlement: calculatedData.bonusEntitlement,
          status: 'draft', // Always save as draft during autosave
          employeeComments,
        };

        if (existingBSCId) {
          // Update existing BSC
          await updateBSC({ id: existingBSCId, body: bscData }).unwrap();
        } else {
          // Create new BSC
          const result = await createBSC(bscData).unwrap();
          setExistingBSCId(result.id || null);
        }
        setLastSaved(new Date());
      } catch (error) {
        console.error('Autosave failed:', error);
      } finally {
        savingRef.current = false;
        setSaving(false);
      }
    }, 15000); // 15 second debounce for seamless typing

    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [perspectives, employeeComments, reviewPeriod, isEditable, autoSaveEnabled, existingBSCId, calculatedData, createBSC, updateBSC]);

  // Update perspectives with calculated scores
  const handleUpdatePerspective = useCallback((index: number, updatedPerspective: BSCPerspectiveScore) => {
    setPerspectives(prev => {
      const newPerspectives = [...prev];
      newPerspectives[index] = updatedPerspective;
      return newPerspectives;
    });
  }, []);

  // Save as draft (manual)
  const handleSaveDraft = async () => {
    if (!reviewPeriod) {
      alert('Please select a review period');
      return;
    }
    
    setSaving(true);
    try {
      const bscData: Partial<BalancedScorecard> = {
        reviewPeriod,
        periodType: 'quarterly',
        perspectives: calculatedData.perspectives,
        totalWeightedScore: calculatedData.totalWeightedScore,
        finalGrade: calculatedData.finalGrade,
        bonusEntitlement: calculatedData.bonusEntitlement,
        status: 'draft',
        employeeComments,
      };

      if (existingBSCId) {
        // Update existing BSC
        await updateBSC({ id: existingBSCId, body: bscData }).unwrap();
        console.log('BSC updated:', existingBSCId);
        setLastSaved(new Date());
        alert('BSC updated!');
      } else {
        // Create new BSC
        const result = await createBSC(bscData).unwrap();
        setExistingBSCId(result.id || null);
        setLastSaved(new Date());
        console.log('BSC saved:', result);
        alert('BSC saved as draft!');
      }
    } catch (error: any) {
      console.error('Error saving BSC:', error);
      const errorMessage = error?.data?.error?.message || error?.data?.message || error?.message || 'Unknown error';
      alert(`Error saving BSC: ${errorMessage}`);
    } finally {
      setSaving(false);
    }
  };

  // Submit for review
  const handleSubmit = async () => {
    if (!reviewPeriod) {
      alert('Please select a review period');
      return;
    }

    // Check if quarter has ended - submission only allowed after quarter ends
    if (!canSubmitQuarter) {
      alert(`Cannot submit yet.\n\n${quarterSubmissionMessage}\n\nYou can save your work as a draft and submit once the quarter has ended.`);
      return;
    }

    // Validate KRA weights - normal KRAs cannot exceed 100%
    const weightValidationErrors: string[] = [];
    calculatedData.perspectives.forEach((perspective) => {
      const normalWeight = perspective.kraScores
        .filter(kra => !kra.isExtraDuty)
        .reduce((sum, kra) => sum + (kra.weight || 0), 0);
      
      if (normalWeight > 100) {
        weightValidationErrors.push(
          `${perspective.name}: Normal goal weights (${normalWeight}%) exceed 100%. Reduce weights or mark some goals as "Extra Duty".`
        );
      }
    });

    if (weightValidationErrors.length > 0) {
      alert(`Cannot submit BSC - Weight Validation Errors:\n\n${weightValidationErrors.join('\n\n')}`);
      return;
    }

    setSaving(true);
    try {
      const bscData: Partial<BalancedScorecard> = {
        reviewPeriod,
        periodType: 'quarterly',
        perspectives: calculatedData.perspectives,
        totalWeightedScore: calculatedData.totalWeightedScore,
        finalGrade: calculatedData.finalGrade,
        bonusEntitlement: calculatedData.bonusEntitlement,
        status: 'submitted',
        employeeComments,
      };

      if (existingBSCId) {
        // Update existing BSC and submit
        await updateBSC({ id: existingBSCId, body: { ...bscData, status: 'submitted' } }).unwrap();
        console.log('BSC submitted:', existingBSCId);
      } else {
        // Create and submit new BSC
        const result = await submitBSC(bscData).unwrap();
        setExistingBSCId(result.id || null);
        console.log('BSC submitted:', result);
      }
      setStatus('submitted');
      alert('BSC submitted for review!');
    } catch (error: any) {
      console.error('Error submitting BSC:', error);
      const errorMessage = error?.data?.error?.message || error?.data?.message || error?.message || 'Unknown error';
      alert(`Error submitting BSC: ${errorMessage}`);
    } finally {
      setSaving(false);
    }
  };

  // Revert to draft (only for submitted but not approved BSCs)
  const handleRevertToDraft = async () => {
    if (!existingBSCId) return;
    
    if (!window.confirm('Are you sure you want to revert this BSC to draft? This will allow you to edit and resubmit it.')) {
      return;
    }

    setSaving(true);
    try {
      await revertToDraft({ id: existingBSCId }).unwrap();
      setStatus('draft');
      alert('BSC reverted to draft. You can now edit and resubmit.');
    } catch (error: any) {
      console.error('Error reverting BSC to draft:', error);
      const errorMessage = error?.data?.error?.message || error?.data?.message || error?.message || 'Unknown error';
      alert(`Error reverting BSC: ${errorMessage}`);
    } finally {
      setSaving(false);
    }
  };

  // Determine if form has been submitted (for UI display)
  const isSubmitted = status === 'submitted' || status === 'approved' || status === 'reviewed';

  if (loadingSubmissions) {
    return (
      <PageWithHeader>
        <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <Typography>Loading...</Typography>
        </Box>
      </PageWithHeader>
    );
  }

  return (
    <PageWithHeader>
      <Box sx={{ p: 3, maxWidth: 1400, margin: '0 auto' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => router.push('/reviews')}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4">Balanced Scorecard</Typography>
            <Chip
              label={status.toUpperCase()}
              color={status === 'approved' ? 'success' : status === 'submitted' ? 'info' : status === 'rejected' ? 'error' : status === 'draft' ? 'warning' : 'default'}
            />
            {existingBSCId && (
              <Chip
                label={isEditable ? 'Editing' : 'View Only'}
                variant="outlined"
                color={isEditable ? 'primary' : 'default'}
                size="small"
              />
            )}
            {/* {autoSaveEnabled && reviewPeriod && isEditable && (
              <Chip
                label={saving ? 'Saving...' : lastSaved ? `Saved ${lastSaved.toLocaleTimeString()}` : 'Autosave enabled'}
                variant="outlined"
                color={saving ? 'warning' : 'success'}
                size="small"
              />
            )} */}
          </Box>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<HistoryIcon />}
              onClick={() => router.push('/reviews/bsc/my-submissions')}
            >
              My BSCs
            </Button>
            <Button
              variant="outlined"
              color="warning"
              startIcon={<RateReviewIcon />}
              onClick={() => router.push('/reviews/bsc/pending-review')}
            >
              Pending
            </Button>
            <Button
              variant="outlined"
              startIcon={<DescriptionIcon />}
              onClick={() => router.push('/reviews/job-descriptions/my')}
            >
              My Job Description
            </Button>
            {isAdmin && (
              <Button
                variant="outlined"
                startIcon={<AccountTreeIcon />}
                onClick={() => router.push('/reviews/job-descriptions')}
              >
                All Job Descriptions
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<SaveIcon />}
              onClick={handleSaveDraft}
              disabled={saving || !isEditable}
            >
              Save Draft
            </Button>
            <Tooltip title={!canSubmitQuarter ? quarterSubmissionMessage : ''} arrow>
              <span>
                <Button
                  variant="contained"
                  startIcon={<SendIcon />}
                  onClick={handleSubmit}
                  disabled={saving || !isEditable || !canSubmitQuarter}
                >
                  Submit
                </Button>
              </span>
            </Tooltip>
            {status === 'submitted' && (
              <Button
                variant="outlined"
                color="warning"
                startIcon={<UndoIcon />}
                onClick={handleRevertToDraft}
                disabled={saving}
              >
                Revert to Draft
              </Button>
            )}
          </Stack>
        </Box>

        {/* Status Alert for submitted BSC */}
        {isSubmitted && (
          <Alert severity="info" sx={{ mb: 3 }}>
            This BSC has been {status}. The form is in view-only mode.
            {status === 'submitted' && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2">
                  Need to make changes? Use the "Revert to Draft" button above to edit and resubmit.
                </Typography>
              </Box>
            )}
            {managerComments && (
              <Box sx={{ mt: 1 }}>
                <strong>Manager Comments:</strong> {managerComments}
              </Box>
            )}
          </Alert>
        )}

        {/* Status Alert for rejected BSC */}
        {status === 'rejected' && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            This BSC was rejected. Please review the manager's comments and resubmit.
            {managerComments && (
              <Box sx={{ mt: 1 }}>
                <strong>Manager Comments:</strong> {managerComments}
              </Box>
            )}
          </Alert>
        )}

        {/* Review Period Selection */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Review Period</InputLabel>
                <Select
                  value={reviewPeriod}
                  onChange={(e) => setReviewPeriod(e.target.value)}
                  label="Review Period"
                >
                  {availableQuarters.map((quarter, index) => (
                    <MenuItem key={index} value={quarter}>
                      {quarter}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={8}>
              <Typography variant="body2" color="text.secondary">
                {availableQuarters.length === 0
                  ? 'All available quarterly BSC reviews have been submitted.'
                  : existingBSCId 
                    ? isEditable 
                      ? 'You have an existing BSC for this period. You can continue editing and submit when ready.'
                      : 'This BSC has already been submitted. Select a different period to create a new submission.'
                    : 'Complete your Balanced Scorecard by filling in goals, measures, and actual performance for each perspective.'
                }
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* Quarter Submission Restriction Warning */}
        {reviewPeriod && !canSubmitQuarter && isEditable && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <strong>Submission Not Yet Available:</strong> {quarterSubmissionMessage}
            <Box sx={{ mt: 1 }}>
              You can continue to prepare your BSC and save it as a draft. Submission will be enabled once the quarter ends.
            </Box>
          </Alert>
        )}

        {/* Score Summary */}
        <ScoreSummary
          perspectives={calculatedData.perspectives}
          totalWeightedScore={calculatedData.totalWeightedScore}
          finalGrade={calculatedData.finalGrade}
          bonusEntitlement={calculatedData.bonusEntitlement}
        />

        {/* Perspective Sections */}
        {perspectives.map((perspective, index) => (
          <PerspectiveSection
            key={perspective.name}
            perspective={calculatedData.perspectives[index]}
            perspectiveIndex={index}
            onUpdatePerspective={handleUpdatePerspective}
            disabled={!isEditable}
          />
        ))}

        {/* Employee Comments */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Employee Comments
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={employeeComments}
            onChange={(e) => setEmployeeComments(e.target.value)}
            placeholder="Add any comments about your performance this period..."
            disabled={!isEditable}
          />
        </Paper>

        {/* Manager Comments (if any) */}
        {managerComments && (
          <Paper sx={{ p: 3, mb: 3, backgroundColor: '#fff8e1' }}>
            <Typography variant="h6" gutterBottom color="warning.main">
              Manager Comments
            </Typography>
            <Typography>{managerComments}</Typography>
          </Paper>
        )}

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => router.push('/home')}
          >
            {isEditable ? 'Cancel' : 'Back'}
          </Button>
          {isEditable && (
            <>
              <Button
                variant="outlined"
                startIcon={<SaveIcon />}
                onClick={handleSaveDraft}
                disabled={saving}
              >
                Save Draft
              </Button>
              <Tooltip title={!canSubmitQuarter ? quarterSubmissionMessage : ''} arrow>
                <span>
                  <Button
                    variant="contained"
                    startIcon={<SendIcon />}
                    onClick={handleSubmit}
                    disabled={saving || !canSubmitQuarter}
                  >
                    Submit for Review
                  </Button>
                </span>
              </Tooltip>
            </>
          )}
        </Box>
      </Box>
    </PageWithHeader>
  );
};

export default BalancedScorecardForm;
